import React from "react"
import styled from "styled-components"
import Layout from "@components/Layout/hoc/withDatoData"
import Helmet from "@components/Helmet"
import Banner from "@components/Banner"
import Empty from "@views/common/Empty"
import Categories from "@views/common/Categories"
import MobileBreadcrumbs from "@views/common/Breadcrumbs/MobileBreadcrumbs"
import DesktopBreadcrumbs from "@views/common/Breadcrumbs/DesktopBreadcrumbs"
import ConfigBanner from "@views/common/Banner/Banner"
import SortDropdown from "@views/common/SortDropdown"
import { SortMethods } from "@views/common"
import FilterButton from "@views/common/FilterButton"
import Filters from "@views/common/Filters"
import Products from "@views/common/Products"
import VerticalSpace from "@components/VerticalSpace"
import useGtmEvents from "@hooks/useGtmEvents"
import useScrollHandler from "@hooks/useScrollHandler"
import ProductStyles from "@views/common/ProductStyles"
import {
  getCarsMakeModelYearUrl,
  getCarsMakeModelYearTrimUrl,
  getCarsMakeModelYearCategoryUrl,
  getCarsMakeModelYearTrimCategoryUrl,
  getClearanceMakeModelYearUrl,
  getClearanceMakeModelYearTrimUrl,
} from "@utils/urls"
import _ from "lodash"
import { useRouter } from "next/router"
import { any } from "ramda"

const productListRef = React.createRef<HTMLDivElement>()

const CategoriesContainer = styled.div`
  margin-top: 20px;
`

const Cars: any = ({
  title,
  make,
  model,
  year,
  trim,
  category,
  data,
  dataLoaded,
  module = "automotive",
}) => {
  const router = useRouter()

  const [type, setType] = React.useState<string>("")
  const [style, setStyle] = React.useState<string>("")
  const [filteredStyles, setFilteredStyles] = React.useState<any>([])

  const allProducts: Array<any> = React.useMemo(() => {
    return data?.allProducts || []
  }, [title, make, model, year, trim, category, data, dataLoaded])
  const productCategories = React.useMemo(() => {
    return _.uniqBy(
      _.flatten(allProducts.map((product) => product.productCategory))
        .filter((category) => !!category)
        .sort(),
      ({ slug }) => slug
    )
  }, [allProducts])

  let productSubcategories
  if (category?.slug == "complete-seats") {
    productSubcategories = React.useMemo(() => {
      return _.uniqBy(
        allProducts
          .map((product) => ({
            image: product.proSeriesSubcategory?.icon?.responsiveImage,
            title: product.proSeriesSubcategory?.name,
            name: product.proSeriesSubcategory?.name,
            slug: product.proSeriesSubcategory?.slug,
          }))
          .filter((subcategory) => !!subcategory.slug)
          .sort(),
        ({ slug }) => slug
      )
    }, [allProducts])
  } else {
    productSubcategories = React.useMemo(() => {
      return _.uniqBy(
        allProducts
          .map((product) => product.productSubcategory)
          .filter((subcategory) => !!subcategory)
          .sort(),
        ({ slug }) => slug
      )
    }, [allProducts])
  }

  const productLines = React.useMemo(() => {
    return _.uniqBy(
      allProducts
        .map((product) => product.productLine)
        .filter((line) => !!line)
        .sort(),
      ({ slug }) => slug
    )
  }, [allProducts])
  const productSizes = React.useMemo(() => {
    return _.uniq(
      allProducts
        .map((product) => product.productSize?.size)
        .filter((size) => !!size)
        .sort()
    )
  }, [allProducts])
  const productStyles = React.useMemo(() => {
    return _.sortBy(
      _.uniqBy(
        allProducts
          .map((product) => ({
            url: product.productStyle?.slug,
            image: product.productStyle?.icon?.responsiveImage,
            title: product.productStyle?.name,
            name: product.productStyle?.name,
            slug: product.productStyle?.slug,
          }))
          .filter((style) => !!style.slug)
          .sort(),
        ({ slug }) => slug
      ),
      ({ name }) => name
    )
  }, [allProducts])
  // console.log(category)

  const reorderByCompleteSeats = (products) => {
    const [a, b] = _.partition(
      products,
      ({ productCategory }) => productCategory.slug === "complete-seats"
    )
    return [...a, ...b]
  }

  const [products, setProducts] = React.useState<any>({
    [SortMethods.ByFeatured]: reorderByCompleteSeats(allProducts),
    [SortMethods.ByPriceAsc]: _.sortBy(allProducts, ({ price }) => price),
    [SortMethods.ByPriceDesc]: _.reverse(
      _.sortBy(allProducts, ({ price }) => price)
    ),
    [SortMethods.ByNameAsc]: _.sortBy(
      allProducts,
      ({ productName }) => productName
    ),
    [SortMethods.ByNameDesc]: _.reverse(
      _.sortBy(allProducts, ({ productName }) => productName)
    ),
  })

  React.useEffect(() => {
    setProducts({
      [SortMethods.ByFeatured]: reorderByCompleteSeats(allProducts),
      [SortMethods.ByPriceAsc]: _.sortBy(allProducts, ({ price }) =>
        parseFloat(price)
      ),
      [SortMethods.ByPriceDesc]: _.reverse(
        _.sortBy(allProducts, ({ price }) => parseFloat(price))
      ),
      [SortMethods.ByNameAsc]: _.sortBy(
        allProducts,
        ({ productName }) => productName
      ),
      [SortMethods.ByNameDesc]: _.reverse(
        _.sortBy(allProducts, ({ productName }) => productName)
      ),
    })
  }, [allProducts])

  const [sortMethod, setSortMethod] = React.useState<SortMethods>(
    SortMethods.ByFeatured
  )

  const onSort = (sortMethod) => {
    //Push state here
    const currentParams = router.query

    const currentPath =
      category && trim
        ? getCarsMakeModelYearTrimCategoryUrl(
            make.slug,
            model.slug,
            year,
            trim.slug,
            category.slug
          )
        : trim
        ? getCarsMakeModelYearTrimUrl(make.slug, model.slug, year, trim.slug)
        : category
        ? getCarsMakeModelYearCategoryUrl(
            make.slug,
            model.slug,
            year,
            category.slug
          )
        : getCarsMakeModelYearUrl(make.slug, model.slug, year)

    delete currentParams.make
    delete currentParams.model
    delete currentParams.year
    delete currentParams.trim
    delete currentParams.trimCategory
    delete currentParams.category

    currentParams["sort"] = sortMethod

    router.push({ pathname: currentPath, query: currentParams }, undefined, {
      shallow: true,
    })

    setSortMethod(sortMethod)
  }

  const [renderedProductCount, setRenderedProductCount] = React.useState(18)

  const onScrollHandler = () => {
    if (
      typeof window === "undefined" ||
      !productListRef ||
      !productListRef.current ||
      allProducts.length <= renderedProductCount
    ) {
      return
    }

    const productListNodes = productListRef.current.childNodes
    const positionReference: any =
      productListNodes[productListNodes.length - 9] ||
      productListNodes[productListNodes.length - 6] ||
      productListNodes[productListNodes.length - 3]

    if (positionReference?.getBoundingClientRect().top < window.innerHeight) {
      setRenderedProductCount(renderedProductCount + 18)
    }
  }

  useScrollHandler({
    onScroll: onScrollHandler,
    runForProps: [data, dataLoaded, renderedProductCount],
  })

  const [renderedProducts, setRenderedProducts] = React.useState(
    _.take(products[SortMethods.ByFeatured], renderedProductCount)
  )

  const filterPropMap = {
    Style: {
      prop: "productStyle",
      key: "name",
    },
    Type: {
      prop: "productSubcategory",
      key: "name",
    },
    Lines: {
      prop: "productLine",
      key: "name",
    },
    Sizes: {
      prop: "productSize",
      key: "size",
    },
  }

  const filters = {
    Type: productSubcategories || null,
    Style: productStyles || null,
    Lines: productLines || null,
    Sizes: productSizes || null,
  } as any

  const selectType = (type) => {
    setType(type.title)
    // console.log(type)

    const newFilters = activeFilters

    newFilters.Type = [type.title]

    setActiveFilters(newFilters)

    const currentPath =
      category && trim
        ? getCarsMakeModelYearTrimCategoryUrl(
            make.slug,
            model.slug,
            year,
            trim.slug,
            category.slug
          )
        : trim
        ? getCarsMakeModelYearTrimUrl(make.slug, model.slug, year, trim.slug)
        : category
        ? getCarsMakeModelYearCategoryUrl(
            make.slug,
            model.slug,
            year,
            category.slug
          )
        : getCarsMakeModelYearUrl(make.slug, model.slug, year)

    let params = {}
    for (var prop in newFilters) {
      if (Object.prototype.hasOwnProperty.call(newFilters, prop)) {
        params[prop] = newFilters[prop]
      }
    }

    //Add sort param if present
    const currentParams = router.query

    if (currentParams.sort) {
      params["sort"] = currentParams.sort
    }

    router.push({ pathname: currentPath, query: params }, undefined, {
      shallow: true,
    })

    setFilteredStyles(
      _.sortBy(
        _.uniqBy(
          (
            allProducts.filter(
              (p: any) => p.proSeriesSubcategory?.name == type
            ) as Array<any>
          )
            .map((product) => ({
              image: product.productStyle?.icon?.responsiveImage,
              title: product.productStyle?.name,
              name: product.productStyle?.name,
              slug: product.productStyle?.slug,
            }))
            .filter((style) => !!style.slug)
            .sort(),
          ({ slug }) => slug
        ),
        ({ name }) => name
      )
    )

    if (typeof window !== "undefined") {
      // console.log(window)
      if (window.screen.width < 768) {
        window.scrollTo({ top: 480, behavior: "smooth" })
      }
    }
  }

  const selectStyle = (style) => {
    setStyle(style)

    const newFilters = activeFilters

    newFilters.Style = [style.title]

    setActiveFilters(newFilters)

    const currentPath =
      category && trim
        ? getCarsMakeModelYearTrimCategoryUrl(
            make.slug,
            model.slug,
            year,
            trim.slug,
            category.slug
          )
        : trim
        ? getCarsMakeModelYearTrimUrl(make.slug, model.slug, year, trim.slug)
        : category
        ? getCarsMakeModelYearCategoryUrl(
            make.slug,
            model.slug,
            year,
            category.slug
          )
        : getCarsMakeModelYearUrl(make.slug, model.slug, year)

    let params = {}
    for (var prop in newFilters) {
      if (Object.prototype.hasOwnProperty.call(newFilters, prop)) {
        params[prop] = newFilters[prop]
      }
    }

    //Add sort param if present
    const currentParams = router.query

    if (currentParams.sort) {
      params["sort"] = currentParams.sort
    }

    router.push({ pathname: currentPath, query: params }, undefined, {
      shallow: true,
    })
  }

  const [activeFilters, setActiveFilters] = React.useState<{
    [filterName: string]: Array<string>
  }>({
    Type: ([] as string[]).concat(
      router.query.Type ? (router.query.Type as string[]) : []
    ),
    Style: ([] as string[]).concat(
      router.query.Style ? (router.query.Style as string[]) : []
    ),
    Lines: ([] as string[]).concat(
      router.query.Lines ? (router.query.Lines as string[]) : []
    ),
    Sizes: ([] as string[]).concat(
      router.query.Sizes ? (router.query.Sizes as string[]) : []
    ),
  })

  const onFilter = (filters) => {
    setActiveFilters(filters)

    const currentPath =
      category && trim
        ? getCarsMakeModelYearTrimCategoryUrl(
            make.slug,
            model.slug,
            year,
            trim.slug,
            category.slug
          )
        : trim
        ? getCarsMakeModelYearTrimUrl(make.slug, model.slug, year, trim.slug)
        : category
        ? getCarsMakeModelYearCategoryUrl(
            make.slug,
            model.slug,
            year,
            category.slug
          )
        : getCarsMakeModelYearUrl(make.slug, model.slug, year)

    //Add push state here
    let params = {}
    for (var prop in filters) {
      if (Object.prototype.hasOwnProperty.call(filters, prop)) {
        params[prop] = filters[prop]
      }
    }

    //Add sort param if present
    const currentParams = router.query

    if (currentParams.sort) {
      params["sort"] = currentParams.sort
    }

    router.push({ pathname: currentPath, query: params }, undefined, {
      shallow: true,
    })
  }

  const onClearFilter = () => {
    setActiveFilters({
      Type: [],
      Style: [],
      Lines: [],
      Sizes: [],
    })
  }

  React.useEffect(() => {
    onClearFilter()
  }, [title, make, model, year, trim, category, data, dataLoaded])

  React.useEffect(() => {
    setActiveFilters({
      Type: ([] as string[]).concat(
        router.query.Type ? (router.query.Type as string[]) : []
      ),
      Style: ([] as string[]).concat(
        router.query.Style ? (router.query.Style as string[]) : []
      ),
      Lines: ([] as string[]).concat(
        router.query.Lines ? (router.query.Lines as string[]) : []
      ),
      Sizes: ([] as string[]).concat(
        router.query.Sizes ? (router.query.Sizes as string[]) : []
      ),
    })

    if (router.query.sort) {
      setSortMethod(router.query.sort as SortMethods)
    }
  }, [router.query])

  React.useEffect(() => {
    const filtersToApply = Object.keys(activeFilters).filter(
      (key) => activeFilters[key].length > 0
    )
    const sortedProducts = products[sortMethod]
    const filteredProducts =
      filtersToApply.length > 0
        ? sortedProducts.filter((product) => {
            let filteredForKey = {}

            filtersToApply.forEach((key) => {
              const useMap = !!filterPropMap[key].key

              if (
                useMap &&
                product[filterPropMap[key].prop] &&
                activeFilters[key].indexOf(
                  product[filterPropMap[key].prop][filterPropMap[key].key]
                ) !== -1
              ) {
                filteredForKey[key] = true
              } else if (
                !useMap &&
                activeFilters[key].indexOf(product[filterPropMap[key].prop]) !==
                  -1
              ) {
                filteredForKey[key] = true
              }
            })

            return (
              Object.values(filteredForKey).filter((v) => !!v).length ===
              filtersToApply.length
            )
          })
        : sortedProducts
    setRenderedProducts(_.take(filteredProducts, renderedProductCount))

    if (router.query.Type) {
      setFilteredStyles(
        _.sortBy(
          _.uniqBy(
            (
              allProducts.filter(
                (p: any) => p.proSeriesSubcategory?.name == router.query.Type
              ) as Array<any>
            )
              .map((product) => ({
                image: product.productStyle?.icon?.responsiveImage,
                title: product.productStyle?.name,
                name: product.productStyle?.name,
                slug: product.productStyle?.slug,
              }))
              .filter((style) => !!style.slug)
              .sort(),
            ({ slug }) => slug
          ),
          ({ name }) => name
        )
      )
    }
  }, [allProducts, products, sortMethod, activeFilters, renderedProductCount])

  const [showMobileFilters, setShowMobileFilters] =
    React.useState<boolean>(false)

  const openMobileFilters = (e) => {
    e.stopPropagation()
    e.preventDefault()
    setShowMobileFilters(true)
  }

  const closeMobileFilters = () => {
    setShowMobileFilters(false)
  }

  let categories, header, subheader, body, breadcrumbs
  const modelsWithTrims = ["Bug", "Ghia", "Watercooled", "Tri-Five"]
  const hasTrims = model && modelsWithTrims.indexOf(model.name) !== -1

  if (make && model && year) {
    if (category && trim) {
      header = category.name
      subheader = `${year} ${make.name} ${model.name} ${trim.name}`
      body = category.bannerBody
      breadcrumbs = [
        {
          link: getCarsMakeModelYearTrimUrl(
            make.slug,
            model.slug,
            year,
            trim.slug
          ),
          label: hasTrims
            ? `${year} ${make.name} ${model.name} ${trim.name}`
            : trim.name,
        },
        {
          link: "#",
          label: category.name,
        },
      ]

      if (!hasTrims) {
        breadcrumbs = [
          {
            link: getCarsMakeModelYearUrl(make.slug, model.slug, year),
            label: `${year} ${make.name} ${model.name}`,
          },
          ,
          ...breadcrumbs,
        ]
      }
    } else if (trim) {
      categories = data?.collectionsConfiguration?.categories
        ?.filter(({ slug }) => {
          const thisCategory = productCategories.find(
            (productCategory) => productCategory.slug === slug
          )
          return !!thisCategory
        })
        .map((category) => ({
          url: getCarsMakeModelYearTrimCategoryUrl(
            make.slug,
            model.slug,
            year,
            trim.slug,
            category.slug
          ),
          image: category.icon?.responsiveImage,
          title: category.name,
        }))
      header = trim.name
      subheader = `${year} ${make.name} ${model.name}`
      body = trim.bannerBody
    } else if (category) {
      header = category.name
      subheader = `${year} ${make.name} ${model.name}`
      body = category.bannerBody
      breadcrumbs = [
        {
          link: getCarsMakeModelYearUrl(make.slug, model.slug, year),
          label: `${year} ${make.name} ${model.name}`,
        },
        {
          link: "#",
          label: category.name,
        },
      ]
    } else {
      categories = data?.collectionsConfiguration?.categories
        ?.filter(({ slug }) => {
          const thisCategory = productCategories.find(
            (productCategory) => productCategory.slug === slug
          )
          return !!thisCategory
        })
        .map((category) => ({
          url: getCarsMakeModelYearCategoryUrl(
            make.slug,
            model.slug,
            year,
            category.slug
          ),
          image: category.icon?.responsiveImage,
          title: category.name,
        }))
      header = `${year} ${make.name} ${model.name}`
      body = model.bannerBody
    }
  }

  const filterCount = activeFilters
    ? Object.keys(activeFilters).reduce((totalCount, key) => {
        return activeFilters[key].length > 0
          ? totalCount + activeFilters[key].length
          : totalCount
      }, 0)
    : 0

  const { onViewCollection } = useGtmEvents()

  React.useEffect(() => {
    if (data && dataLoaded) {
      onViewCollection({
        make: make?.name,
        model: model?.name,
        year: year,
        trim: trim?.name,
        category: category?.name,
      })
    }
  }, [data, dataLoaded])

  return (
    <Layout module={module}>
      <Helmet
        title={title || data?.page?.title || "TMI Products"}
        module={module}
      />
      <VerticalSpace height={20} mobileHeight={32} />
      <Banner
        backgroundImage={
          model?.bannerImage?.responsiveImage ||
          data?.collectionsConfiguration?.defaultBannerImage?.responsiveImage
        }
        header={header}
        subheader={subheader}
        body={body}
      />
      {categories && categories.length > 0 && (
        <CategoriesContainer>
          <Categories
            categories={categories}
            galleryLink={
              data?._allGalleriesMeta?.count >= 1
                ? `/gallery?make=${make.name}&model=${model.name}`
                : undefined
            }
            clearanceLink={
              module == "powersports"
                ? undefined
                : trim
                ? getClearanceMakeModelYearTrimUrl(
                    make.slug,
                    model.slug,
                    year,
                    trim.slug
                  )
                : getClearanceMakeModelYearUrl(make.slug, model.slug, year)
            }
          />
        </CategoriesContainer>
      )}
      <VerticalSpace height={32} mobileHeight={24} />
      {breadcrumbs && (
        <>
          <MobileBreadcrumbs breadcrumbs={breadcrumbs} />
          <VerticalSpace height={0} mobileHeight={20} />
        </>
      )}
      {products[sortMethod] && products[sortMethod].length > 0 && (
        <SortDropdown
          value={sortMethod}
          onSort={onSort}
          galleryLink={
            data?._allGalleriesMeta?.count >= 1
              ? `/gallery?make=${make.name}&model=${model.name}`
              : undefined
          }
          clearanceLink={
            module == "powersports"
              ? undefined
              : trim
              ? getClearanceMakeModelYearTrimUrl(
                  make.slug,
                  model.slug,
                  year,
                  trim.slug
                )
              : getClearanceMakeModelYearUrl(make.slug, model.slug, year)
          }
        />
      )}
      {breadcrumbs && <DesktopBreadcrumbs breadcrumbs={breadcrumbs} />}
      <VerticalSpace height={32} mobileHeight={0} />
      {!!category && (
        <FilterButton filterCount={filterCount} onClick={openMobileFilters} />
      )}
      {category?.slug == "complete-seats" ? (
        !type ? (
          <ProductStyles
            productStyles={productSubcategories}
            onClick={selectType}
          />
        ) : (
          <ProductStyles
            productStyles={type ? filteredStyles : productStyles}
            onClick={selectStyle}
          />
        )
      ) : (
        ""
      )}
      <Products
        containerRef={productListRef}
        make={make?.name}
        model={model?.name}
        year={year}
        trim={trim?.name}
        showFilters={!!category}
        filters={filters}
        activeFilters={activeFilters}
        onFilter={onFilter}
        onClearFilters={onClearFilter}
        products={renderedProducts}
        showEachForSingleSoldAs
        defaultProductImage={
          data?.collectionsConfiguration?.defaultProductCardImage
            ?.responsiveImage
        }
      />
      {dataLoaded && allProducts?.length === 0 && <Empty />}
      <VerticalSpace height={84} mobileHeight={28} />
      {!!category && (
        <Filters
          isOpen={showMobileFilters}
          filters={filters}
          activeFilters={activeFilters}
          onFilter={onFilter}
          onClearFilters={onClearFilter}
          onRequestClose={closeMobileFilters}
        />
      )}

      {category?.slug == "complete-seats" ? <ConfigBanner /> : ""}
    </Layout>
  )
}

export default Cars
